import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";

export type ExportNovedadesTarget = "excel" | "system";
export type SystemExportStatus =
    "not_configured" |
    "ready" |
    "preparing_export_data" |
    "data_validation" |
    "file_generation" |
    "completed" |
    "validation_failed";

export interface SystemExportValidationRow {
    id: string;
    rowIndex: string;
    employeeID: string;
    concept: string;
    error: string;
}

interface UseExportNovedadesExportStepViewModelProps {
    process: NovedadesExportServiceModel.ProcessRecord;
    service: NovedadesExportService;
}

interface ExportNovedadesExcelStatusColumn {
    totalsField: string;
    label: string;
}

const sanitizeFilename = (value: string): string => {
    return value.trim().replace(/[\\/:*?"<>|]/g, "-") || "exportacion";
};

const roundDecimal = (value: number): number => {
    return Math.round(value * 100) / 100;
};

const minutesToHours = (minutes: number | undefined): number => {
    return roundDecimal((minutes ?? 0) / 60);
};

const employeeDisplayName = (
    employee: NovedadesExportServiceModel.ProcessEmployee,
): string => {
    return employee.name || employee.displayName || employee.employeeID || "N/D";
};

const buildWorksheetRows = (
    employees: NovedadesExportServiceModel.ProcessEmployee[],
    statusColumns: ExportNovedadesExcelStatusColumn[],
    includeWorkedHours: boolean,
) => {
    return employees.map((employee) => {
        const totals = employee.totals ?? {};
        const extraMinutes = (totals.extra50Minutes ?? 0) + (totals.extra100Minutes ?? 0);
        const row: Record<string, string | number> = {
            ["Colaborador".localized()]: employeeDisplayName(employee),
            ["Legajo".localized()]: employee.legajo || employee.payrollEmployeeID || "",
        };

        if (includeWorkedHours) {
            row["Horas trabajadas".localized()] = minutesToHours(totals.totalMinutes);
            row["Horas extra".localized()] = minutesToHours(extraMinutes);
        }

        statusColumns.forEach((column) => {
            row[column.label.localized()] = roundDecimal(totals[column.totalsField] ?? 0);
        });

        return row;
    });
};

const applyHeaderStyle = (worksheet: XLSX.WorkSheet, columnCount: number) => {
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: columnIndex });
        const cell = worksheet[cellAddress];
        if (!cell) {
            continue;
        }

        cell.s = {
            fill: {
                fgColor: { rgb: "4A4A4A" },
            },
            font: {
                color: { rgb: "FFFFFF" },
                bold: true,
            },
        };
    }
};

const downloadWorkbook = (
    process: NovedadesExportServiceModel.ProcessRecord,
    employees: NovedadesExportServiceModel.ProcessEmployee[],
    statusColumns: ExportNovedadesExcelStatusColumn[],
    includeWorkedHours: boolean,
) => {
    const fixedColumnCount = includeWorkedHours ? 4 : 2;
    const columnCount = fixedColumnCount + statusColumns.length;
    const worksheet = XLSX.utils.json_to_sheet(
        buildWorksheetRows(employees, statusColumns, includeWorkedHours),
    );
    worksheet["!cols"] = [
        { wch: 32 },
        { wch: 16 },
        ...(includeWorkedHours ? [{ wch: 18 }, { wch: 14 }] : []),
        ...statusColumns.map((column) => ({
            wch: Math.max(12, Math.min(24, column.label.length + 2)),
        })),
    ];
    worksheet["!autofilter"] = {
        ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: employees.length, c: Math.max(0, columnCount - 1) },
        }),
    };
    applyHeaderStyle(worksheet, columnCount);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Colaboradores");

    const xlsBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([xlsBuffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${sanitizeFilename(process.title)}.xlsx`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const bucketNameForEnvironment = (): string => {
    return process.env.REACT_APP_FRONT_ENV === "QA"
        ? "fixit-qa.appspot.com"
        : "fixit-prod-141b2.appspot.com";
};

const storageURLForPath = (filePath: string): string => {
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
        return filePath;
    }

    return `https://storage.googleapis.com/${bucketNameForEnvironment()}/${encodeURI(filePath)}`;
};

const downloadURL = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const validationRowsFromProcessErrors = (
    errors: NovedadesExportServiceModel.ProcessLastError[],
): SystemExportValidationRow[] => {
    return (errors ?? []).map((error, errorIndex) => ({
        id: `${error.rowID || "process"}-${errorIndex}`,
        rowIndex: error.rowIndex === undefined ? "N/D" : String(error.rowIndex),
        employeeID: error.employeeID || "N/D",
        concept: error.novedadAcronym || error.errorType || "N/D",
        error: processLastErrorMessage(error),
    }));
};

const processLastErrorMessage = (error: NovedadesExportServiceModel.ProcessLastError): string => {
    if (error.errorType === "MISSING_PAYROLL_CODE" && error.novedadAcronym) {
        return `${"Falta configurar el código de nómina para la novedad".localized()} ${error.novedadAcronym}`;
    }
    return error.message;
};

export const useExportNovedadesExportStepViewModel = ({
    process,
    service,
}: UseExportNovedadesExportStepViewModelProps) => {
    const preferredSystemProfileID = process.exportSystem || process.calculationProfileID;
    const [selectedTarget, setSelectedTargetState] = useState<ExportNovedadesTarget>(
        process.outputFilePath ? "system" : "excel",
    );
    const [error, setError] = useState<string | null>(null);
    const [systemExportMessage, setSystemExportMessage] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isGeneratingSystemFile, setIsGeneratingSystemFile] = useState(false);
    const [hasRequestedSystemGeneration, setHasRequestedSystemGeneration] = useState(false);
    const [isLoadingValidationRows, setIsLoadingValidationRows] = useState(false);
    const [validationRows, setValidationRows] = useState<SystemExportValidationRow[]>([]);
    const [selectedSystemProfileID, setSelectedSystemProfileIDState] = useState<string | undefined>(
        preferredSystemProfileID,
    );
    const [includeWorkedHours, setIncludeWorkedHoursState] = useState<boolean>(
        process.includeWorkedHours ?? true,
    );
    const [selectedColppyTipo, setSelectedColppyTipoState] = useState<string>("M");
    const [exportProfiles, setExportProfiles] = useState<NovedadesExportServiceModel.ExportProfile[]>([]);
    const [exportProfilesLoading, setExportProfilesLoading] = useState(false);
    const [hasLoadedExportProfiles, setHasLoadedExportProfiles] = useState(false);
    const [exportProfilesError, setExportProfilesError] = useState<string | null>(null);
    const validationErrorsAnalyticsKeyRef = useRef<string | undefined>(undefined);
    const lastKnownProcessIncludeWorkedHoursRef = useRef(process.includeWorkedHours);

    useEffect(() => {
        if (
            process.includeWorkedHours !== undefined &&
            process.includeWorkedHours !== lastKnownProcessIncludeWorkedHoursRef.current
        ) {
            setIncludeWorkedHoursState(process.includeWorkedHours);
        }
        lastKnownProcessIncludeWorkedHoursRef.current = process.includeWorkedHours;
    }, [process.includeWorkedHours]);

    useEffect(() => {
        if (!process.outputFilePath) {
            return;
        }

        setSelectedTargetState("system");
        if (preferredSystemProfileID) {
            setSelectedSystemProfileIDState(preferredSystemProfileID);
        }
    }, [preferredSystemProfileID, process.outputFilePath, process.processID]);

    const systemExportStatus = useMemo<SystemExportStatus>(() => {
        if (!selectedSystemProfileID) {
            return "not_configured";
        }

        if (
            (process.status === "EXPORT_GENERATED" || process.status === "COMPLETED") &&
            process.currentStep === "FILE_CREATED" &&
            Boolean(process.outputFilePath)
        ) {
            return "completed";
        }

        if (process.status === "RUNNING" && process.currentStep === "PREPARING_EXPORT_DATA") {
            return "preparing_export_data";
        }

        if (process.status === "RUNNING" && process.currentStep === "DATA_VALIDATION") {
            return "data_validation";
        }

        if (process.status === "RUNNING" && process.currentStep === "FILE_GENERATION") {
            return "file_generation";
        }

        if (process.status === "PARTIALLY_FAILED" && process.currentStep === "DATA_VALIDATION") {
            return "validation_failed";
        }

        if (hasRequestedSystemGeneration || isGeneratingSystemFile) {
            return "preparing_export_data";
        }

        return "ready";
    }, [hasRequestedSystemGeneration, isGeneratingSystemFile, process, selectedSystemProfileID]);

    useEffect(() => {
        if (
            process.status === "EXPORT_GENERATED" ||
            process.status === "COMPLETED" ||
            process.status === "PARTIALLY_FAILED"
        ) {
            setHasRequestedSystemGeneration(false);
        }
    }, [process.status]);

    useEffect(() => {
        if (selectedTarget !== "system" || !process.organizationID) {
            return;
        }

        let cancelled = false;

        const loadProfiles = async () => {
            setExportProfilesLoading(true);
            setExportProfilesError(null);

            try {
                const profiles = await service.listProfiles(process.organizationID);
                if (cancelled) {
                    return;
                }

                setExportProfiles(profiles);
                setSelectedSystemProfileIDState((currentProfileID) => {
                    if (currentProfileID && profiles.some((profile) => profile.profileID === currentProfileID)) {
                        return currentProfileID;
                    }
                    return process.exportSystem || process.calculationProfileID || profiles[0]?.profileID;
                });
            } catch (loadError) {
                console.error(loadError);
                if (!cancelled) {
                    setExportProfiles((currentProfiles) => currentProfiles);
                    setExportProfilesError("No se pudieron cargar los perfiles de exportación.".localized());
                }
            } finally {
                if (!cancelled) {
                    setHasLoadedExportProfiles(true);
                    setExportProfilesLoading(false);
                }
            }
        };

        void loadProfiles();

        return () => {
            cancelled = true;
        };
    }, [process.calculationProfileID, process.exportSystem, process.organizationID, selectedTarget, service]);

    useEffect(() => {
        if (
            selectedTarget !== "system" ||
            systemExportStatus !== "validation_failed" ||
            !process.processID
        ) {
            setValidationRows([]);
            return;
        }

        setIsLoadingValidationRows(false);
        setValidationRows(validationRowsFromProcessErrors(process.lastErrors));
        const lastErrorsCount = process.lastErrorsCount ?? process.lastErrors.length;
        const analyticsKey = `${process.processID}:${process.errorsCount ?? 0}:${lastErrorsCount}`;
        if (validationErrorsAnalyticsKeyRef.current !== analyticsKey) {
            validationErrorsAnalyticsKeyRef.current = analyticsKey;
            NovedadesExportAnalyticsHelper.trackValidationErrorsShown({
                processID: process.processID,
                errorsCount: process.errorsCount ?? 0,
                lastErrorsCount,
            });
        }
    }, [process.errorsCount, process.lastErrors, process.lastErrorsCount, process.processID, selectedTarget, systemExportStatus]);

    const setSelectedTarget = useCallback((target: ExportNovedadesTarget) => {
        setSelectedTargetState(target);
        NovedadesExportAnalyticsHelper.trackExportTargetSelected({
            processID: process.processID,
            target,
        });
    }, [process.processID]);

    const setSelectedSystemProfileID = useCallback((profileID: string) => {
        setSelectedSystemProfileIDState(profileID);
        NovedadesExportAnalyticsHelper.trackSystemProfileSelected({
            processID: process.processID,
            profileID,
        });
    }, [process.processID]);

    const setSelectedColppyTipo = useCallback((tipo: string) => {
        setSelectedColppyTipoState(tipo);
        NovedadesExportAnalyticsHelper.trackColppyTipoSelected({
            processID: process.processID,
            tipo,
        });
    }, [process.processID]);

    const setIncludeWorkedHours = useCallback((include: boolean) => {
        setIncludeWorkedHoursState(include);
        NovedadesExportAnalyticsHelper.trackIncludeWorkedHoursToggled({
            processID: process.processID,
            includeWorkedHours: include,
        });
    }, [process.processID]);

    const exportToExcel = useCallback(async () => {
        if (!process.processID || selectedTarget !== "excel") {
            return;
        }

        setIsExporting(true);
        setError(null);
        NovedadesExportAnalyticsHelper.trackExcelExportSubmitted({
            processID: process.processID,
            includeWorkedHours,
        });
        try {
            const [employees, configuration] = await Promise.all([
                service.listEmployees(process.processID),
                service.getStatusMappingConfiguration(process.organizationID),
            ]);
            const statusColumns = configuration.groups.map((group) => ({
                totalsField: group.totalsField,
                label: group.label,
            }));
            downloadWorkbook(process, employees, statusColumns, includeWorkedHours);
            NovedadesExportAnalyticsHelper.trackExcelExportSucceeded({
                processID: process.processID,
                employeeCount: employees.length,
                statusColumnCount: statusColumns.length,
                customStatusColumnCount: configuration.groups.filter((group) => group.builtIn !== true).length,
                includeWorkedHours,
            });
        } catch (exportError) {
            console.error(exportError);
            NovedadesExportAnalyticsHelper.trackExcelExportFailed({
                processID: process.processID,
                includeWorkedHours,
            });
            setError("No se pudo generar el Excel.".localized());
        } finally {
            setIsExporting(false);
        }
    }, [includeWorkedHours, process, selectedTarget, service]);

    const generateSystemFile = useCallback(async (isRestart = false): Promise<boolean> => {
        if (!process.processID || selectedTarget !== "system" || !selectedSystemProfileID) {
            return false;
        }

        const isColppySystemSelected = selectedSystemProfileID === "COLPPY";
        setIsGeneratingSystemFile(true);
        setHasRequestedSystemGeneration(true);
        setError(null);
        setSystemExportMessage(null);
        const analyticsPayload = {
            processID: process.processID,
            profileID: selectedSystemProfileID,
            includeWorkedHours,
        };
        if (isRestart) {
            NovedadesExportAnalyticsHelper.trackSystemFileRestartSubmitted(analyticsPayload);
        } else {
            NovedadesExportAnalyticsHelper.trackSystemFileGenerationSubmitted(analyticsPayload);
        }
        try {
            await service.generateExportFile(process.processID, {
                exportSystem: selectedSystemProfileID,
                ...(isColppySystemSelected ? { exportTipo: selectedColppyTipo } : {}),
                includeWorkedHours,
            });
            if (isRestart) {
                NovedadesExportAnalyticsHelper.trackSystemFileRestartStarted(analyticsPayload);
            } else {
                NovedadesExportAnalyticsHelper.trackSystemFileGenerationStarted(analyticsPayload);
            }
            return true;
        } catch (exportError) {
            console.error(exportError);
            setHasRequestedSystemGeneration(false);
            if (isRestart) {
                NovedadesExportAnalyticsHelper.trackSystemFileRestartFailed(analyticsPayload);
            } else {
                NovedadesExportAnalyticsHelper.trackSystemFileGenerationFailed(analyticsPayload);
            }
            setError("No se pudo iniciar la generación del archivo.".localized());
            return false;
        } finally {
            setIsGeneratingSystemFile(false);
        }
    }, [includeWorkedHours, process.processID, selectedColppyTipo, selectedSystemProfileID, selectedTarget, service]);

    const downloadGeneratedSystemFile = useCallback(() => {
        if (!process.outputFilePath) {
            setError("No se encontró el archivo generado.".localized());
            return;
        }

        downloadURL(
            storageURLForPath(process.outputFilePath),
            process.outputFilename || `${sanitizeFilename(process.title)}.txt`,
        );
        NovedadesExportAnalyticsHelper.trackSystemFileDownloaded({
            processID: process.processID,
            profileID: selectedSystemProfileID,
            outputFilenamePresent: Boolean(process.outputFilename),
        });
    }, [process, selectedSystemProfileID]);

    return {
        selectedTarget,
        setSelectedTarget,
        error,
        systemExportMessage,
        systemExportStatus,
        selectedSystemProfileID,
        setSelectedSystemProfileID,
        selectedColppyTipo,
        setSelectedColppyTipo,
        isColppyTipoSelectionVisible: selectedSystemProfileID === "COLPPY",
        includeWorkedHours,
        setIncludeWorkedHours,
        exportProfiles,
        exportProfilesLoading,
        isLoadingExportProfilesForFirstTime: exportProfilesLoading && !hasLoadedExportProfiles && exportProfiles.length === 0,
        exportProfilesError,
        validationRows,
        isLoadingValidationRows,
        isExporting,
        isGeneratingSystemFile,
        exportToExcel,
        generateSystemFile,
        downloadGeneratedSystemFile,
        canExportToExcel: selectedTarget === "excel" && !isExporting,
        canGenerateSystemFile: selectedTarget === "system" && Boolean(selectedSystemProfileID) && !isGeneratingSystemFile,
        canDownloadGeneratedSystemFile: (
            (process.status === "EXPORT_GENERATED" || process.status === "COMPLETED") &&
            process.currentStep === "FILE_CREATED" &&
            Boolean(process.outputFilePath)
        ),
    };
};
